import React, { useEffect, useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  TextField, 
  Button, 
  Table, 
  TableHead, 
  TableBody, 
  TableCell, 
  TableRow, 
  Pagination, 
  Select, 
  MenuItem, 
  InputLabel, 
  FormControl,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Autocomplete,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Collapse,
  Divider,
  TableSortLabel
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Modal from "react-bootstrap/Modal";
import { 
  faEdit, 
  faDownload, 
  faCheck, 
  faTimes, 
  faTrash, 
  faPrint, 
  faEye,
  faCalendarAlt,
  faSearch,
  faImage,
  faFileInvoice,
  faFilePdf,
  faFileImage,
  faFile,
  faSync,
  faExclamationTriangle,
  faFilter,
  faChevronDown,
  faChevronUp,
  faBan,
  faUser,
  faBuilding,
  faMoneyBill,
  faHashtag,
  faFileAlt,
  faCreditCard,
  faSort,
  faSortUp,
  faSortDown,
  faFileArchive,
  faSortAmountUp,
  faSortAmountDown
} from '@fortawesome/free-solid-svg-icons';
import 'bootstrap/dist/css/bootstrap.min.css';
import { styled } from '@mui/material/styles';
import ApiServices from "../../../actions/services";

// Componente para convertir PDF a imagen usando CDN
const PDFToImageConverter = ({ base64PDF, onConversionComplete, onError }) => {
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState(null);
  const [pdfjsLib, setPdfjsLib] = useState(null);

  // Cargar pdf.js dinámicamente desde CDN
  useEffect(() => {
    const loadPDFJS = async () => {
      try {
        // Verificar si ya está cargado
        if (window.pdfjsLib) {
          setPdfjsLib(window.pdfjsLib);
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          return;
        }

        // Crear script para cargar pdf.js
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.async = true;
        
        script.onload = () => {
          if (window.pdfjsLib) {
            setPdfjsLib(window.pdfjsLib);
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
              'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          } else {
            onError('No se pudo cargar la biblioteca PDF.js desde CDN');
          }
        };
        
        script.onerror = () => {
          onError('No se pudo cargar la biblioteca PDF.js desde CDN');
        };
        
        document.head.appendChild(script);
      } catch (err) {
        console.error('Error al cargar PDF.js:', err);
        onError('Error al cargar biblioteca PDF');
      }
    };

    loadPDFJS();
  }, [onError]);

  useEffect(() => {
    const convertPDFToImage = async () => {
      if (!base64PDF || !pdfjsLib) return;

      setConverting(true);
      setError(null);

      try {
        // Decodificar base64
        const binaryString = atob(base64PDF);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Cargar PDF
        const loadingTask = pdfjsLib.getDocument({ data: bytes });
        const pdf = await loadingTask.promise;
        
        // Obtener primera página
        const page = await pdf.getPage(1);
        
        // Configurar viewport
        const viewport = page.getViewport({ scale: 1.5 });
        
        // Crear canvas
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        // Renderizar página en canvas
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };
        
        await page.render(renderContext).promise;
        
        // Convertir canvas a base64 (JPG)
        const imageBase64 = canvas.toDataURL('image/jpeg', 0.8);
        
        // Extraer solo la parte base64
        const base64ImageData = imageBase64.split(',')[1];
        
        onConversionComplete(base64ImageData, 'jpg');
        
      } catch (err) {
        console.error('Error al convertir PDF a imagen:', err);
        const errorMsg = `Error al convertir PDF: ${err.message || 'Error desconocido'}`;
        setError(errorMsg);
        onError(errorMsg);
      } finally {
        setConverting(false);
      }
    };

    if (pdfjsLib && base64PDF) {
      convertPDFToImage();
    }
  }, [base64PDF, pdfjsLib, onConversionComplete, onError]);

  if (!pdfjsLib) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={2}>
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Cargando biblioteca PDF...
        </Typography>
      </Box>
    );
  }

  if (converting) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={2}>
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Convirtiendo PDF a imagen...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="body2">
          No se pudo convertir el PDF a imagen. Se mostrará información del PDF.
        </Typography>
      </Alert>
    );
  }

  return null;
};

// Componente simplificado para PDF (sin conversión si falla CDN)
const SimplePDFConverter = ({ base64PDF, onConversionComplete, onError }) => {
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    const trySimpleConversion = async () => {
      if (!base64PDF) return;
      
      setConverting(true);
      
      try {
        // Intentar método alternativo simple
        // Crear iframe temporal para renderizar PDF
        const tempIframe = document.createElement('iframe');
        tempIframe.style.display = 'none';
        tempIframe.srcdoc = `
          <!DOCTYPE html>
          <html>
          <head>
            <script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js"></script>
            <script>
              window.onload = async function() {
                try {
                  const base64 = "${base64PDF}";
                  const binaryString = atob(base64);
                  const bytes = new Uint8Array(binaryString.length);
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                  }
                  
                  pdfjsLib.GlobalWorkerOptions.workerSrc = 
                    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
                  
                  const loadingTask = pdfjsLib.getDocument({ data: bytes });
                  const pdf = await loadingTask.promise;
                  const page = await pdf.getPage(1);
                  
                  const viewport = page.getViewport({ scale: 1.5 });
                  const canvas = document.createElement('canvas');
                  const context = canvas.getContext('2d');
                  
                  canvas.height = viewport.height;
                  canvas.width = viewport.width;
                  
                  await page.render({
                    canvasContext: context,
                    viewport: viewport
                  }).promise;
                  
                  const imageData = canvas.toDataURL('image/jpeg', 0.8);
                  window.parent.postMessage({
                    type: 'PDF_CONVERSION_COMPLETE',
                    data: imageData.split(',')[1]
                  }, '*');
                  
                } catch (error) {
                  window.parent.postMessage({
                    type: 'PDF_CONVERSION_ERROR',
                    error: error.message
                  }, '*');
                }
              };
            </script>
          </head>
          <body>
            <div id="pdf-container"></div>
          </body>
          </html>
        `;
        
        document.body.appendChild(tempIframe);
        
        // Escuchar mensajes del iframe
        const messageHandler = (event) => {
          if (event.data.type === 'PDF_CONVERSION_COMPLETE') {
            onConversionComplete(event.data.data, 'jpg');
            document.body.removeChild(tempIframe);
            window.removeEventListener('message', messageHandler);
          } else if (event.data.type === 'PDF_CONVERSION_ERROR') {
            onError(event.data.error);
            document.body.removeChild(tempIframe);
            window.removeEventListener('message', messageHandler);
          }
        };
        
        window.addEventListener('message', messageHandler);
        
        // Timeout por seguridad
        setTimeout(() => {
          if (converting) {
            onError('Tiempo de espera agotado para conversión de PDF');
            document.body.removeChild(tempIframe);
            window.removeEventListener('message', messageHandler);
          }
        }, 30000);
        
      } catch (err) {
        console.error('Error en conversión simple:', err);
        onError('No se pudo convertir el PDF');
      } finally {
        setConverting(false);
      }
    };

    trySimpleConversion();
    
    return () => {
      // Limpiar
      const iframes = document.querySelectorAll('iframe[style*="display: none"]');
      iframes.forEach(iframe => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      });
    };
  }, [base64PDF, onConversionComplete, onError]);

  if (converting) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={2}>
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Preparando conversión de PDF...
        </Typography>
      </Box>
    );
  }

  return null;
};

// Componente para la vista previa de imagen o PDF
const FilePreview = ({ base64, extension, filename, fileType }) => {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (!base64 || !extension) return null;

  const isPDF = extension.toLowerCase() === 'pdf';
  const fileUrl = isPDF 
    ? `data:application/pdf;base64,${base64}`
    : `data:image/${extension};base64,${base64}`;

  const getFileIcon = () => {
    if (isPDF) return faFilePdf;
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension.toLowerCase())) {
      return faFileImage;
    }
    return faFile;
  };

  return (
    <>
      <Tooltip title={`Ver ${isPDF ? 'PDF' : 'imagen'}`}>
        <IconButton 
          onClick={handleClickOpen}
          size="small"
          color={isPDF ? "error" : "primary"}
        >
          <FontAwesomeIcon icon={getFileIcon()} />
        </IconButton>
      </Tooltip>
      
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <FontAwesomeIcon 
              icon={getFileIcon()} 
              style={{ 
                marginRight: '10px',
                color: isPDF ? '#f44336' : '#2196f3'
              }} 
            />
            Vista previa: {filename || (isPDF ? 'Documento PDF' : 'Imagen del abono')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" sx={{ height: '70vh' }}>
            {isPDF ? (
              <iframe 
                src={fileUrl}
                title="PDF Preview"
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              />
            ) : (
              <img 
                src={fileUrl} 
                alt="Imagen del abono"
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cerrar
          </Button>
          <Button 
            onClick={() => {
              const link = document.createElement('a');
              link.href = fileUrl;
              link.download = `${filename || (isPDF ? 'documento' : 'imagen_abono')}.${extension}`;
              link.click();
            }}
            color="primary"
            startIcon={<FontAwesomeIcon icon={faDownload} />}
          >
            Descargar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Componente mejorado para el informe de impresión (ajustado para una sola hoja)
const PrintReport = ({ item, open, onClose }) => {
  const [pdfImage, setPdfImage] = useState(null);
  const [conversionError, setConversionError] = useState(null);
  const [converting, setConverting] = useState(false);
  const [useSimpleConverter, setUseSimpleConverter] = useState(false);

  useEffect(() => {
    if (item && item.B64Extension?.toLowerCase() === 'pdf' && item.B64Imagen && !pdfImage && !conversionError) {
      setConverting(true);
      setConversionError(null);
      
      // Intentar método alternativo si el CDN falla
      const timer = setTimeout(() => {
        if (converting) {
          setUseSimpleConverter(true);
        }
      }, 5000);
      
      return () => clearTimeout(timer);
    } else {
      setPdfImage(null);
      setConverting(false);
      setUseSimpleConverter(false);
    }
  }, [item, pdfImage, conversionError, converting]);

  if (!item) return null;

  const isPDF = item.B64Extension?.toLowerCase() === 'pdf';
  const fileUrl = item.B64Imagen && item.B64Extension 
    ? isPDF 
      ? `data:application/pdf;base64,${item.B64Imagen}`
      : `data:image/${item.B64Extension};base64,${item.B64Imagen}`
    : null;

  const imageUrl = isPDF && pdfImage 
    ? `data:image/jpeg;base64,${pdfImage}`
    : !isPDF && fileUrl;

  const handleConversionComplete = (imageBase64, extension) => {
    setPdfImage(imageBase64);
    setConverting(false);
  };

  const handleConversionError = (error) => {
    setConversionError(error);
    setConverting(false);
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const [datePart, timePart] = dateTimeString.split('T');
    const date = new Date(datePart + (timePart ? 'T' + timePart : ''));
    
    return date.toLocaleDateString('es-GT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-GT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Función para imprimir ajustado a una sola hoja
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Por favor, permite ventanas emergentes para imprimir');
      return;
    }

    const content = document.getElementById('printable-report').innerHTML;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Informe Abono #${item.acov_codigo}</title>
        <style>
          @page {
            size: letter;
            margin: 0.5cm;
          }
          body {
            margin: 0;
            padding: 0;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 10pt;
            color: #333;
            width: 100%;
            max-width: 100%;
          }
          .report-container {
            width: 100%;
            max-width: 100%;
            padding: 10px;
            box-sizing: border-box;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #1976d2;
          }
          .header h1 {
            color: #1976d2;
            font-size: 18pt;
            margin: 5px 0;
          }
          .header .subtitle {
            color: #666;
            font-size: 11pt;
            margin-bottom: 10px;
          }
          .section {
            margin-bottom: 15px;
            page-break-inside: avoid;
          }
          .section-title {
            background-color: #f5f5f5;
            padding: 5px 10px;
            border-left: 4px solid #1976d2;
            margin-bottom: 8px;
            font-weight: bold;
            font-size: 11pt;
          }
          .info-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 8px;
            font-size: 9pt;
          }
          .info-item {
            margin-bottom: 5px;
          }
          .info-label {
            font-weight: bold;
            color: #555;
            font-size: 8pt;
          }
          .info-value {
            color: #333;
            font-size: 9pt;
          }
          .chip {
            display: inline-block;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 8pt;
            margin: 2px;
          }
          .chip-success { background-color: #e8f5e9; color: #2e7d32; border: 1px solid #c8e6c9; }
          .chip-warning { background-color: #fff3e0; color: #ef6c00; border: 1px solid #ffe0b2; }
          .chip-error { background-color: #ffebee; color: #c62828; border: 1px solid #ffcdd2; }
          .chip-info { background-color: #e3f2fd; color: #1565c0; border: 1px solid #bbdefb; }
          .image-container {
            text-align: center;
            margin: 10px 0;
            page-break-inside: avoid;
          }
          .image-container img {
            max-width: 100%;
            max-height: 300px;
            border: 1px solid #ddd;
            border-radius: 4px;
          }
          .footer {
            margin-top: 20px;
            padding-top: 10px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 8pt;
            color: #666;
          }
          .compact-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9pt;
          }
          .compact-table th {
            background-color: #f5f5f5;
            padding: 4px;
            text-align: left;
            border-bottom: 1px solid #ddd;
            font-weight: bold;
          }
          .compact-table td {
            padding: 4px;
            border-bottom: 1px solid #eee;
          }
          .amount-highlight {
            font-weight: bold;
            color: #2e7d32;
          }
          .note-box {
            background-color: #fffde7;
            border-left: 4px solid #ffd600;
            padding: 8px;
            margin: 10px 0;
            font-size: 9pt;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; }
            .no-print { display: none; }
            .page-break { page-break-before: always; }
          }
        </style>
      </head>
      <body>
        <div class="report-container">
          <div class="header">
            <h1>REPORTE DE ABONO</h1>
            <div class="subtitle">Sistema de Validación de Abonos</div>
            ${isPDF ? `<span class="chip chip-error">DOCUMENTO PDF</span>` : ''}
            ${isPDF && pdfImage ? `<span class="chip chip-info">CONVERTIDO A IMAGEN</span>` : ''}
          </div>
          
          <div class="section">
            <div class="section-title">Información del Abono</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Código:</div>
                <div class="info-value">#${item.acov_codigo}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Fecha y Hora:</div>
                <div class="info-value">${formatDateTime(item.Fecha)}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Estado:</div>
                <div class="info-value">
                  <span class="chip ${
                    item.Proceso === 'Validado' ? 'chip-success' : 
                    item.Proceso === 'Pendiente de Validar' ? 'chip-warning' : 'chip-error'
                  }">
                    ${item.Proceso}
                  </span>
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Monto:</div>
                <div class="info-value amount-highlight">
                  Q ${parseFloat(item.Monto).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Referencia:</div>
                <div class="info-value">${item.NoReferencia}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Forma de Pago:</div>
                <div class="info-value">${item.FormaPago}</div>
              </div>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">Información del Cliente</div>
            <table class="compact-table">
              <tr>
                <th width="30%">Código:</th>
                <td>${item.CodigoCliente}</td>
              </tr>
              <tr>
                <th>Nombre:</th>
                <td><strong>${item.NombreCliente}</strong></td>
              </tr>
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">Documentos Relacionados</div>
            <table class="compact-table">
              <tr>
                <th width="30%">Banco:</th>
                <td>${item.Banco}</td>
              </tr>
              <tr>
                <th>Orden de Venta:</th>
                <td><strong>${item.OV}</strong></td>
              </tr>
              <tr>
                <th>Factura SAP:</th>
                <td>${item.Factura_DocNum || 'N/A'}</td>
              </tr>
               <tr>
                <th>Factura:</th>
                <td>${item.Factura_DocNum || 'N/A'}</td>
              </tr>
              <tr>
                <th>Factura FEL:</th>
                <td>${item.Factura_FEL || 'N/A'}</td>
              </tr>
              <tr>
                <th>Validador:</th>
                <td>${item.UsuarioValidador || 'Pendiente'}</td>
              </tr>
            </table>
          </div>
          
          ${(imageUrl || (isPDF && !conversionError)) ? `
            <div class="section">
              <div class="section-title">
                ${isPDF ? 'Documento Adjunto' : 'Comprobante de Pago'}
                ${isPDF && pdfImage ? '<span class="chip chip-info">Convertido a Imagen</span>' : ''}
              </div>
              
              ${isPDF && !pdfImage && !conversionError ? `
                <div class="note-box">
                  <strong>⚠ El PDF se está convirtiendo a imagen para mejor impresión...</strong>
                  <div style="margin-top: 5px;">
                    <span class="chip chip-error">Documento PDF - Conversión en proceso</span>
                  </div>
                </div>
              ` : ''}
              
              ${imageUrl ? `
                <div class="image-container">
                  <img src="${imageUrl}" alt="${isPDF ? 'Primera página del PDF convertida' : 'Comprobante de pago'}" />
                  ${isPDF ? `
                    <div class="note-box">
                      <strong>Nota:</strong> Se muestra la primera página del PDF convertida a imagen para mejor compatibilidad con la impresión.
                    </div>
                  ` : ''}
                </div>
                <div style="text-align: center; font-size: 8pt; color: #666; margin-top: 5px;">
                  ${isPDF ? 'Documento PDF' : 'Comprobante'} del abono #${item.acov_codigo}
                  ${isPDF ? `<br>${pdfImage ? 'Convertido a imagen JPEG para impresión' : 'Formato PDF original'}` : ''}
                </div>
              ` : ''}
            </div>
          ` : ''}
          
          ${isPDF && conversionError ? `
            <div class="section">
              <div class="note-box" style="background-color: #fff3e0; border-left-color: #ff9800;">
                <strong>⚠ No se pudo convertir el PDF a imagen.</strong>
                <div style="margin-top: 5px; font-size: 8pt;">
                  El PDF original está disponible pero puede no imprimirse correctamente.
                  Se recomienda descargar el PDF e imprimirlo por separado.
                </div>
              </div>
            </div>
          ` : ''}
          
          <div class="footer">
            Generado el ${formatDateTime(new Date().toISOString())} - 
            Sistema de Validación de Abonos © ${new Date().getFullYear()}
            ${isPDF ? `<br>${pdfImage ? 'PDF convertido automáticamente a imagen para impresión.' : 'Documento PDF incluido.'}` : ''}
          </div>
        </div>
        
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() {
              window.close();
            }, 1000);
          };
        </script>
      </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            <FontAwesomeIcon 
              icon={isPDF ? faFilePdf : faFileInvoice} 
              style={{ marginRight: '10px', color: isPDF ? '#f44336' : '#1976d2' }} 
            />
            Informe del Abono #{item.acov_codigo}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePrint}
            startIcon={<FontAwesomeIcon icon={faPrint} />}
            disabled={converting}
          >
            {converting ? 'Convirtiendo...' : 'Imprimir'}
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Conversión automática de PDF a imagen */}
        {isPDF && item.B64Imagen && !pdfImage && !conversionError && (
          <>
            {useSimpleConverter ? (
              <SimplePDFConverter
                base64PDF={item.B64Imagen}
                onConversionComplete={handleConversionComplete}
                onError={handleConversionError}
              />
            ) : (
              <PDFToImageConverter
                base64PDF={item.B64Imagen}
                onConversionComplete={handleConversionComplete}
                onError={handleConversionError}
              />
            )}
          </>
        )}

        {converting && !useSimpleConverter && (
          <Box display="flex" alignItems="center" justifyContent="center" p={3}>
            <CircularProgress size={30} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Convirtiendo PDF a imagen para el informe...
            </Typography>
          </Box>
        )}

        {conversionError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {conversionError}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              El informe incluirá información sobre el PDF sin la imagen.
            </Typography>
          </Alert>
        )}

        <Paper 
          elevation={3} 
          style={{ 
            padding: '30px',
            marginTop: '20px',
            backgroundColor: '#f8f9fa'
          }}
          id="printable-report"
          className="no-print"
        >
          {/* Encabezado */}
          <Box textAlign="center" mb={4}>
            <Typography variant="h5" gutterBottom style={{ color: '#1976d2', fontWeight: 'bold' }}>
              <FontAwesomeIcon icon={faFileInvoice} style={{ marginRight: '10px' }} />
              REPORTE DE ABONO
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Sistema de Validación de Abonos
            </Typography>
            {isPDF && (
              <Box display="flex" alignItems="center" justifyContent="center" mt={1} flexWrap="wrap" gap={1}>
                <Chip 
                  icon={<FontAwesomeIcon icon={faFilePdf} />}
                  label="DOCUMENTO PDF"
                  color="error"
                  variant="outlined"
                />
                {pdfImage && (
                  <Chip 
                    icon={<FontAwesomeIcon icon={faSync} />}
                    label="CONVERTIDO A IMAGEN"
                    color="info"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            )}
          </Box>

          <Grid container spacing={3}>
            {/* Información del Abono */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom style={{ color: '#2c3e50' }}>
                    <FontAwesomeIcon icon={faFileInvoice} style={{ marginRight: '10px' }} />
                    Información del Abono
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="subtitle2" color="textSecondary">Código:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        #{item.acov_codigo}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="subtitle2" color="textSecondary">Fecha y Hora:</Typography>
                      <Typography variant="body1">
                        {formatDateTime(item.Fecha)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="subtitle2" color="textSecondary">Estado:</Typography>
                      <Chip 
                        label={item.Proceso}
                        color={
                          item.Proceso === 'Validado' ? 'success' : 
                          item.Proceso === 'Pendiente de Validar' ? 'warning' : 'error'
                        }
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="subtitle2" color="textSecondary">Monto:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold', color: '#2e7d32' }}>
                        Q {parseFloat(item.Monto).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="subtitle2" color="textSecondary">Referencia:</Typography>
                      <Typography variant="body1">{item.NoReferencia}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="subtitle2" color="textSecondary">Forma de Pago:</Typography>
                      <Typography variant="body1">{item.FormaPago}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Información del Cliente */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom style={{ color: '#2c3e50' }}>
                    Información del Cliente
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">Código:</Typography>
                      <Typography variant="body1">{item.CodigoCliente}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">Nombre:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        {item.NombreCliente}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Información de Pago y Documentos */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom style={{ color: '#2c3e50' }}>
                    Documentos Relacionados
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">Banco:</Typography>
                      <Typography variant="body1">{item.Banco}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">Orden de Venta:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        {item.OV}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Factura SAP:</Typography>
                      <Typography variant="body1">{item.Factura_DocNum || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Factura :</Typography>
                      <Typography variant="body1">{item.Factura_DocNum || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Factura FEL:</Typography>
                      <Typography variant="body1">{item.Factura_FEL || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">Validador:</Typography>
                      <Typography variant="body1">{item.UsuarioValidador || 'Pendiente'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Documento Adjunto - Con conversión automática */}
            {(imageUrl || (isPDF && !conversionError)) && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom style={{ color: '#2c3e50' }}>
                      <FontAwesomeIcon 
                        icon={isPDF ? faFilePdf : faImage} 
                        style={{ marginRight: '10px', color: isPDF ? '#f44336' : '#2196f3' }} 
                      />
                      {isPDF ? 'Documento Adjunto' : 'Comprobante de Pago'}
                      {isPDF && pdfImage && (
                        <Chip 
                          label="Convertido a Imagen"
                          color="info"
                          size="small"
                          sx={{ ml: 1, fontSize: '0.75rem' }}
                        />
                      )}
                    </Typography>
                    
                    <Box textAlign="center">
                      {isPDF && !pdfImage && !conversionError ? (
                        <Box>
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                              <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '8px' }} />
                              El PDF se está convirtiendo a imagen para mejor impresión...
                            </Typography>
                          </Alert>
                          <Chip 
                            icon={<FontAwesomeIcon icon={faFilePdf} />}
                            label="Documento PDF - Conversión en proceso"
                            color="error"
                            variant="outlined"
                            sx={{ mb: 2 }}
                          />
                        </Box>
                      ) : imageUrl ? (
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <img 
                            src={imageUrl} 
                            alt={isPDF ? "Primera página del PDF convertida" : "Comprobante de pago"}
                            style={{ 
                              maxWidth: '100%',
                              maxHeight: '500px',
                              border: '1px solid #ddd',
                              borderRadius: '8px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              marginBottom: '15px'
                            }}
                          />
                          {isPDF && (
                            <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
                              <Typography variant="body2">
                                <strong>Nota:</strong> Se muestra la primera página del PDF convertida a imagen 
                                para mejor compatibilidad con la impresión.
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                      ) : null}
                    </Box>
                    
                    <Box textAlign="center" mt={2}>
                      <Typography variant="caption" color="textSecondary">
                        {isPDF ? 'Documento PDF' : 'Comprobante'} del abono #{item.acov_codigo}
                      </Typography>
                      {isPDF && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {pdfImage ? 'Convertido a imagen JPEG para impresión' : 'Formato PDF original'}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Información si hay error en conversión */}
            {isPDF && conversionError && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Alert severity="warning">
                      <Typography variant="body2">
                        <strong>No se pudo convertir el PDF a imagen.</strong> 
                        El PDF original está disponible pero puede no imprimirse correctamente.
                        Se recomienda descargar el PDF e imprimirlo por separado.
                      </Typography>
                      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button 
                          size="small" 
                          color="primary" 
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = fileUrl;
                            link.download = `abono_${item.acov_codigo}.pdf`;
                            link.click();
                          }}
                        >
                          <FontAwesomeIcon icon={faDownload} style={{ marginRight: '5px' }} />
                          Descargar PDF original
                        </Button>
                        <Button 
                          size="small" 
                          color="secondary"
                          onClick={() => window.open(fileUrl, '_blank')}
                        >
                          <FontAwesomeIcon icon={faEye} style={{ marginRight: '5px' }} />
                          Ver PDF en nueva ventana
                        </Button>
                      </Box>
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Pie de página */}
            <Grid item xs={12}>
              <Box 
                mt={4} 
                pt={3} 
                borderTop={1} 
                borderColor="divider"
                textAlign="center"
              >
                <Typography variant="caption" color="textSecondary">
                  Generado el {formatDateTime(new Date().toISOString())} - 
                  Sistema de Validación de Abonos © {new Date().getFullYear()}
                </Typography>
                {isPDF && (
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                    {pdfImage ? 'PDF convertido automáticamente a imagen para impresión.' : 'Documento PDF incluido.'}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={converting}>
          Cerrar
        </Button>
        {isPDF && fileUrl && (
          <Button 
            onClick={() => {
              const link = document.createElement('a');
              link.href = fileUrl;
              link.download = `abono_${item.acov_codigo}.pdf`;
              link.click();
            }}
            color="error"
            startIcon={<FontAwesomeIcon icon={faDownload} />}
          >
            Descargar PDF
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Función para obtener fechas de la semana actual
const getCurrentWeekDates = () => {
  const now = new Date();
  const currentDay = now.getDay(); // 0 = Domingo, 1 = Lunes, ...
  const diff = currentDay === 0 ? 6 : currentDay - 1; // Ajuste para que la semana empiece en Lunes
  
  const monday = new Date(now);
  monday.setDate(now.getDate() - diff);
  
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4); // Semana laboral de Lunes a Viernes
  
  // Formatear fechas como YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    startDate: formatDate(monday),
    endDate: formatDate(friday)
  };
};

// Función para obtener color de fondo según extensión
const getRowColorByExtension = (extension) => {
  if (!extension) return 'transparent';
  
  const ext = extension.toLowerCase();
  if (ext === 'pdf') {
    return 'rgba(244, 67, 54, 0.05)'; // Rojo suave para PDF
  } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return 'rgba(33, 150, 243, 0.05)'; // Azul suave para imágenes
  } else {
    return 'rgba(158, 158, 158, 0.05)'; // Gris suave para otros formatos
  }
};

// Función para obtener color de borde según extensión
const getRowBorderColorByExtension = (extension) => {
  if (!extension) return 'transparent';
  
  const ext = extension.toLowerCase();
  if (ext === 'pdf') {
    return '1px solid rgba(244, 67, 54, 0.2)';
  } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return '1px solid rgba(33, 150, 243, 0.2)';
  } else {
    return '1px solid rgba(158, 158, 158, 0.2)';
  }
};

// Función para obtener icono según extensión
const getExtensionIcon = (extension) => {
  if (!extension) return faFile;
  
  const ext = extension.toLowerCase();
  if (ext === 'pdf') {
    return faFilePdf;
  } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return faFileImage;
  } else {
    return faFile;
  }
};

// Función para obtener color de icono según extensión
const getExtensionIconColor = (extension) => {
  if (!extension) return '#757575';
  
  const ext = extension.toLowerCase();
  if (ext === 'pdf') {
    return '#f44336';
  } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return '#2196f3';
  } else {
    return '#757575';
  }
};

// Componente de filtros avanzados
const AdvancedFilters = ({ 
  datosGenerados, 
  filtros, 
  onFiltrosChange, 
  camposDisponibles,
  open,
  onToggle 
}) => {
  const [filtroTemporal, setFiltroTemporal] = useState(filtros);

  const handleCampoChange = (campo, checked) => {
    setFiltroTemporal(prev => ({
      ...prev,
      camposSeleccionados: checked 
        ? [...prev.camposSeleccionados, campo]
        : prev.camposSeleccionados.filter(c => c !== campo)
    }));
  };

  const handleValorChange = (campo, valor) => {
    setFiltroTemporal(prev => ({
      ...prev,
      valoresFiltro: {
        ...prev.valoresFiltro,
        [campo]: valor
      }
    }));
  };

  const handleTipoArchivoChange = (tipo) => {
    setFiltroTemporal(prev => ({
      ...prev,
      tipoArchivo: tipo
    }));
  };

  const aplicarFiltros = () => {
    onFiltrosChange(filtroTemporal);
  };

  const limpiarFiltros = () => {
    const filtrosLimpiados = {
      textoGeneral: '',
      camposSeleccionados: ['acov_codigo', 'OV', 'NombreCliente', 'NoReferencia'],
      valoresFiltro: {},
      tipoArchivo: ''
    };
    setFiltroTemporal(filtrosLimpiados);
    onFiltrosChange(filtrosLimpiados);
  };

  // Obtener valores únicos para cada campo (para autocomplete)
  const getValoresUnicos = (campo) => {
    if (!datosGenerados.length) return [];
    const valores = datosGenerados
      .map(item => item[campo])
      .filter(valor => valor != null && valor.toString().trim() !== '');
    return [...new Set(valores)].sort();
  };

  // Obtener tipos de archivo únicos
  const getTiposArchivoUnicos = () => {
    if (!datosGenerados.length) return [];
    const tipos = datosGenerados
      .map(item => item.B64Extension)
      .filter(ext => ext != null && ext.toString().trim() !== '')
      .map(ext => ext.toLowerCase());
    
    const tiposUnicos = [...new Set(tipos)];
    
    // Ordenar y mapear a nombres legibles
    return tiposUnicos.sort().map(ext => ({
      value: ext,
      label: ext === 'pdf' ? 'PDF' : ext.toUpperCase(),
      icon: ext === 'pdf' ? faFilePdf : faFileImage
    }));
  };

  return (
    <Collapse in={open}>
      <Paper elevation={2} sx={{ p: 3, mt: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FontAwesomeIcon icon={faFilter} />
            Filtros Avanzados
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={limpiarFiltros}
              startIcon={<FontAwesomeIcon icon={faBan} />}
            >
              Limpiar
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={aplicarFiltros}
              startIcon={<FontAwesomeIcon icon={faCheck} />}
            >
              Aplicar
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* Filtro general */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Búsqueda general"
              value={filtroTemporal.textoGeneral}
              onChange={(e) => setFiltroTemporal(prev => ({ ...prev, textoGeneral: e.target.value }))}
              placeholder="Buscar en todos los campos..."
              size="small"
              InputProps={{
                startAdornment: (
                  <FontAwesomeIcon 
                    icon={faSearch} 
                    style={{ marginRight: '8px', color: '#666' }}
                  />
                ),
              }}
            />
          </Grid>

          {/* Filtro por tipo de archivo */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Archivo</InputLabel>
              <Select
                value={filtroTemporal.tipoArchivo || ''}
                onChange={(e) => handleTipoArchivoChange(e.target.value)}
                label="Tipo de Archivo"
              >
                <MenuItem value="">Todos los tipos</MenuItem>
                {getTiposArchivoUnicos().map(tipo => (
                  <MenuItem key={tipo.value} value={tipo.value}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <FontAwesomeIcon 
                        icon={tipo.icon} 
                        style={{ 
                          color: tipo.value === 'pdf' ? '#f44336' : '#2196f3',
                          fontSize: '14px'
                        }} 
                      />
                      <span>{tipo.label}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Selección de campos a filtrar */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Campos a incluir en la búsqueda:
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, maxHeight: '200px', overflow: 'auto' }}>
              <Grid container spacing={1}>
                {camposDisponibles.map((campo) => (
                  <Grid item xs={12} sm={6} md={4} key={campo}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filtroTemporal.camposSeleccionados.includes(campo)}
                          onChange={(e) => handleCampoChange(campo, e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <FontAwesomeIcon 
                            icon={
                              campo.includes('codigo') || campo.includes('Codigo') ? faHashtag :
                              campo.includes('Nombre') || campo.includes('nombre') ? faUser :
                              campo.includes('Banco') ? faBuilding :
                              campo.includes('Monto') ? faMoneyBill :
                              campo.includes('Factura') ? faFileAlt :
                              campo.includes('Pago') ? faCreditCard :
                              campo.includes('Fecha') ? faCalendarAlt :
                              faFile
                            }
                            style={{ fontSize: '12px', color: '#666' }}
                          />
                          <Typography variant="body2">
                            {campo.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Filtros específicos por campo */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Filtros específicos:
            </Typography>
            <Grid container spacing={2}>
              {filtroTemporal.camposSeleccionados.map((campo) => {
                const valoresUnicos = getValoresUnicos(campo);
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={`filter-${campo}`}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={valoresUnicos}
                      value={filtroTemporal.valoresFiltro[campo] || ''}
                      onChange={(event, newValue) => handleValorChange(campo, newValue || '')}
                      onInputChange={(event, newInputValue) => handleValorChange(campo, newInputValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={campo.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          placeholder={`Filtrar por ${campo}`}
                        />
                      )}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Collapse>
  );
};

// Componente estilizado para el botón
const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 'bold',
}));

// Función para normalizar Banco cuando FormaPago es Visalink
const normalizarBancoVisalink = (item) => {
  if (item.FormaPago && item.FormaPago.toLowerCase().includes('visalink')) {
    return 'NeoNet';
  }
  return item.Banco || '';
};

function Abono() {
  const [datosGenerados, setDatosGenerados] = useState([]);
  const [datosOriginales, setDatosOriginales] = useState([]); // Para mantener los datos originales
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [paginaActual, setPaginaActual] = useState(1);
  const [elementosPorPagina, setElementosPorPagina] = useState(50); // Cambiado a 50 por defecto
  const [show, setShow] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [codigoSeleccionado, setCodigoSeleccionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const handleClose = () => setShow(false);
  const { token, user } = useSelector(state => state.auth);

  const [showModal, setShowModal] = useState(false);
  const [comentario, setComentario] = useState(''); 
  const [showModal2, setShowModal2] = useState(false);
  const [printReportOpen, setPrintReportOpen] = useState(false);
  const [selectedItemForPrint, setSelectedItemForPrint] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filtrosAvanzados, setFiltrosAvanzados] = useState({
    textoGeneral: '',
    camposSeleccionados: ['acov_codigo', 'OV', 'NombreCliente', 'NoReferencia'],
    valoresFiltro: {},
    tipoArchivo: ''
  });

  // Estado para ordenamiento
  const [ordenamiento, setOrdenamiento] = useState({
    campo: 'acov_codigo',
    direccion: 'asc'
  });

  // Campos disponibles para filtrar (se actualizarán con los datos recibidos)
  const [camposDisponibles, setCamposDisponibles] = useState([
    'acov_codigo',
    'Banco',
    'FormaPago',
    'OV',
    'Factura_DocNum',
    'Factura_FEL',
    'CodigoCliente',
    'NombreCliente',
    'NoReferencia',
    'Monto',
    'Fecha',
    'Proceso',
    'UsuarioValidador'
  ]);

  // Establecer fechas de la semana actual al iniciar
  useEffect(() => {
    const { startDate, endDate } = getCurrentWeekDates();
    setFechaInicio(startDate);
    setFechaFin(endDate);
  }, []);

  // Actualizar campos disponibles cuando se reciben datos
  useEffect(() => {
    if (datosOriginales.length > 0) {
      const primerItem = datosOriginales[0];
      const nuevosCampos = Object.keys(primerItem).filter(
        campo => !campo.startsWith('B64') && campo !== 'id'
      );
      setCamposDisponibles(nuevosCampos);
    }
  }, [datosOriginales]);

  const handleShow = (codigo) => {
    setCodigoSeleccionado(codigo);
    setShow(true);
  };

  const handlePrintReport = (item) => {
    setSelectedItemForPrint(item);
    setPrintReportOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAccept = async () => {
    console.log('Código:', codigoSeleccionado);
    const idusuarioenvia = user.id_usuario;

    const metodo = "ValidarAbono";
    const datosRecibidos = [{ acov_tipo: "1", acov_codigo: codigoSeleccionado, acov_usuario: idusuarioenvia }];

    try {
      const response = await ApiServices.post(metodo, datosRecibidos, user);

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      const resultadoAPI = await response.json();
      setResultado(resultadoAPI);
      console.log('Respuesta de la API:', resultadoAPI);
      
      setSnackbar({
        open: true,
        message: 'Abono validado exitosamente',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error en la petición:', error);
      setError(error.message);
      setSnackbar({
        open: true,
        message: `Error al validar abono: ${error.message}`,
        severity: 'error'
      });
    }

    handleClose();
    fetchData(); // Actualizar los datos después de validar
  };

  const handlecancelAbono = async () => {
    console.log('Código:', codigoSeleccionado);
    const idusuarioenvia = user.id_usuario;

    const metodo = "ValidarAbono";
    const datosRecibidos = [{ acov_tipo: "2", acov_codigo: codigoSeleccionado, acov_usuario: idusuarioenvia, acov_motivo: comentario }];

    try {
      const response = await ApiServices.post(metodo, datosRecibidos, user);

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      const resultadoAPI = await response.json();
      setResultado(resultadoAPI);
      console.log('Respuesta de la API:', resultadoAPI);
      
      setSnackbar({
        open: true,
        message: 'Abono cancelado exitosamente',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error en la petición:', error);
      setError(error.message);
      setSnackbar({
        open: true,
        message: `Error al cancelar abono: ${error.message}`,
        severity: 'error'
      });
    }

    handleCloseModal();
    fetchData(); // Actualizar los datos después de cancelar
  };

  const handleTrashedAbono = async () => {
    console.log('Código:', codigoSeleccionado);
    const idusuarioenvia = user.id_usuario;

    const metodo = "ValidarAbono";
    const datosRecibidos = [{ acov_tipo: "3", acov_codigo: codigoSeleccionado, acov_usuario: idusuarioenvia, acov_motivo: comentario }];

    try {
      const response = await ApiServices.post(metodo, datosRecibidos, user);

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      const resultadoAPI = await response.json();
      setResultado(resultadoAPI);
      console.log('Respuesta de la API:', resultadoAPI);
      
      setSnackbar({
        open: true,
        message: 'Abono eliminado exitosamente',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error en la petición:', error);
      setError(error.message);
      setSnackbar({
        open: true,
        message: `Error al eliminar abono: ${error.message}`,
        severity: 'error'
      });
    }

    handleCloseModal2();
    fetchData(); // Actualizar los datos después de eliminar
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const idusuarioenvia = String(user.id_usuario);
      const nameuser = user.usuario;
      
      if (!idusuarioenvia || !nameuser) {
        throw new Error('Datos de usuario no disponibles');
      }
  
      if (!fechaInicio || !fechaFin) {
        throw new Error('Debe seleccionar un rango de fechas');
      }
  
      const data = await ApiServices.get("ListaAbonos", {FechaIni: fechaInicio, FechaFin: fechaFin}, user);

      console.log("Datos Generados desde el API", data);
      
      if (data.datos_Generados) {
        const datosGeneradosDecodificados = JSON.parse(data.datos_Generados);
        console.log(datosGeneradosDecodificados);
        
        if (datosGeneradosDecodificados?.LISTABONO) {
          // Eliminar duplicados basados en acov_codigo
          const abonosUnicos = Array.from(
            new Map(
              datosGeneradosDecodificados.LISTABONO.map(item => [item.acov_codigo, item])
            ).values()
          );
          setDatosOriginales(abonosUnicos);
          // Aplicar ordenamiento inicial
          const datosOrdenados = ordenarDatos(abonosUnicos, ordenamiento.campo, ordenamiento.direccion);
          setDatosGenerados(datosOrdenados);
        } else {
          setDatosOriginales([]);
          setDatosGenerados([]);
          console.warn('La respuesta no contiene datos de abonos');
        }
      } else {
        setDatosOriginales([]);
        setDatosGenerados([]);
        console.warn('La respuesta no contiene datos_Generados');
      }
    } catch (error) {
      console.error('Error al obtener los abonos:', error);
      setError(error.message);
      setDatosOriginales([]);
      setDatosGenerados([]);
      setSnackbar({
        open: true,
        message: `Error al cargar abonos: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para ordenar datos
  const ordenarDatos = (datos, campo, direccion) => {
    return [...datos].sort((a, b) => {
      let valorA = a[campo];
      let valorB = b[campo];
      
      // Manejar valores nulos o undefined
      if (valorA == null) valorA = '';
      if (valorB == null) valorB = '';
      
      // Convertir a string para comparación
      valorA = valorA.toString().toLowerCase();
      valorB = valorB.toString().toLowerCase();
      
      // Manejar campos numéricos
      if (campo === 'acov_codigo' || campo === 'Monto') {
        valorA = parseFloat(valorA) || 0;
        valorB = parseFloat(valorB) || 0;
      }
      
      // Manejar fechas
      if (campo === 'Fecha') {
        valorA = new Date(valorA).getTime();
        valorB = new Date(valorB).getTime();
      }
      
      if (direccion === 'asc') {
        return valorA < valorB ? -1 : valorA > valorB ? 1 : 0;
      } else {
        return valorA > valorB ? -1 : valorA < valorB ? 1 : 0;
      }
    });
  };

  // Manejar ordenamiento de columnas
  const handleOrdenar = (campo) => {
    const esAscendente = ordenamiento.campo === campo && ordenamiento.direccion === 'asc';
    const nuevaDireccion = esAscendente ? 'desc' : 'asc';
    
    setOrdenamiento({
      campo,
      direccion: nuevaDireccion
    });
    
    // Ordenar los datos actuales
    const datosOrdenados = ordenarDatos(datosGenerados, campo, nuevaDireccion);
    setDatosGenerados(datosOrdenados);
  };

  // Aplicar filtros cuando cambian los filtros avanzados
  useEffect(() => {
    aplicarFiltrosAvanzados();
  }, [filtrosAvanzados, datosOriginales, ordenamiento]);

  const aplicarFiltrosAvanzados = () => {
    if (!datosOriginales.length) {
      setDatosGenerados([]);
      return;
    }

    const { textoGeneral, camposSeleccionados, valoresFiltro, tipoArchivo } = filtrosAvanzados;
    
    let datosFiltrados = datosOriginales.filter(item => {
      // Filtro por tipo de archivo
      if (tipoArchivo) {
        const extension = item.B64Extension?.toLowerCase() || '';
        if (extension !== tipoArchivo.toLowerCase()) return false;
      }

      // Filtro por texto general (busca en todos los campos seleccionados)
      if (textoGeneral.trim()) {
        const texto = textoGeneral.toLowerCase();
        const coincideTextoGeneral = camposSeleccionados.some(campo => {
          const valor = item[campo];
          if (valor == null) return false;
          return valor.toString().toLowerCase().includes(texto);
        });
        
        if (!coincideTextoGeneral) return false;
      }

      // Filtros específicos por campo
      for (const [campo, valorFiltro] of Object.entries(valoresFiltro)) {
        if (valorFiltro && valorFiltro.toString().trim() !== '') {
          const valorItem = item[campo];
          if (valorItem == null) return false;
          
          const valorBusqueda = valorFiltro.toString().toLowerCase();
          const valorComparar = valorItem.toString().toLowerCase();
          
          if (!valorComparar.includes(valorBusqueda)) return false;
        }
      }

      // Filtro por estado (si está activo)
      if (filtroEstado && item.Proceso !== filtroEstado) return false;

      return true;
    });

    // Aplicar ordenamiento después del filtrado
    datosFiltrados = ordenarDatos(datosFiltrados, ordenamiento.campo, ordenamiento.direccion);

    setDatosGenerados(datosFiltrados);
    setPaginaActual(1); // Resetear a la primera página
  };

  const handleSearch = () => {
    fetchData();
  };

  const handlePageChange = (event, value) => {
    setPaginaActual(value);
  };

  const handleElementosPorPaginaChange = (event) => {
    setElementosPorPagina(parseInt(event.target.value, 10));
    setPaginaActual(1); // Resetear a la primera página
  };

  // Aplicar filtro de estado cuando cambia
  useEffect(() => {
    aplicarFiltrosAvanzados();
  }, [filtroEstado]);

  const filteredData = datosGenerados; // Ya están filtrados por aplicarFiltrosAvanzados

  const indexUltimoElemento = paginaActual * elementosPorPagina;
  const indexPrimerElemento = indexUltimoElemento - elementosPorPagina;
  const datosPaginados = filteredData.slice(indexPrimerElemento, indexUltimoElemento);

  const descargarArchivo = (base64, extension, nombreArchivo) => {
    const isPDF = extension?.toLowerCase() === 'pdf';
    const mimeType = isPDF ? 'application/pdf' : `image/${extension}`;
    const fileUrl = `data:${mimeType};base64,${base64}`;
    const enlace = document.createElement('a');
    enlace.href = fileUrl;
    enlace.download = `${nombreArchivo}.${extension}`;
    enlace.click();
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const [datePart, timePart] = dateTimeString.split('T');
    const date = new Date(datePart + (timePart ? 'T' + timePart : ''));
    
    return (
      <Box>
        <Typography variant="body2">
          {date.toLocaleDateString('es-GT', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {timePart ? timePart.substring(0, 5) : '00:00'}
        </Typography>
      </Box>
    );
  };

  const formatMonto = (monto) => {
    const amount = parseFloat(monto);
    return (
      <Box>
        <Typography sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
          Q {amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        </Typography>
        {amount >= 10000 && (
          <Chip 
            label="Alto Monto"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ mt: 0.5, fontSize: '0.6rem' }}
          />
        )}
      </Box>
    );
  };

  const formatOVFactura = (item) => {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <FontAwesomeIcon icon={faFileInvoice} style={{ fontSize: '12px', color: '#1976d2' }} />
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {item.OV}
          </Typography>
        </Box>
        
        <Box mt={0.5}>
          {item.Factura_DocNum && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Chip 
                label="SAP" 
                size="small" 
                color="primary" 
                variant="outlined"
                sx={{ height: '20px', fontSize: '0.65rem' }}
              />
              <Typography variant="caption">
                {item.Factura_DocNum}
              </Typography>
            </Box>
          )}
          
          {item.Factura_FEL && (
            <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
              <Chip 
                label="FEL" 
                size="small" 
                color="secondary" 
                variant="outlined"
                sx={{ height: '20px', fontSize: '0.65rem' }}
              />
              <Typography variant="caption">
                {item.Factura_FEL}
              </Typography>
            </Box>
          )}
           {item.U_DoctoNo && (
            <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
              <Chip 
                label="SAT" 
                size="small" 
                color="warning" 
                variant="outlined"
                sx={{ height: '20px', fontSize: '0.65rem' }}
              />
              <Typography variant="caption">
                {item.U_DoctoNo}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const handlePasarAbono = (codigo) => {
    setCodigoSeleccionado(codigo);
    setShow(true);
  };

  const handleCancelarAbono = (codigo) => {
    setCodigoSeleccionado(codigo);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setComentario('');
  };

  const handleCloseModal2 = () => {
    setShowModal2(false);
    setComentario('');
  };

  const handleTrashAbono = (codigo) => {
    setCodigoSeleccionado(codigo);
    setShowModal2(true);
  };

  // Estadísticas de los datos
  const estadisticas = useMemo(() => {
    if (!datosOriginales.length) return null;
    
    const totalAbonos = datosOriginales.length;
    const totalMonto = datosOriginales.reduce((sum, item) => sum + parseFloat(item.Monto || 0), 0);
    const pendientes = datosOriginales.filter(item => item.Proceso === 'Pendiente de Validar').length;
    const validados = datosOriginales.filter(item => item.Proceso === 'Validado').length;
    
    return {
      totalAbonos,
      totalMonto: totalMonto.toFixed(2),
      pendientes,
      validados,
      porcentajeValidados: totalAbonos > 0 ? ((validados / totalAbonos) * 100).toFixed(1) : 0
    };
  }, [datosOriginales]);

  // Componente para cabecera de tabla con ordenamiento
  const SortableHeader = ({ campo, label, children, ...props }) => {
    const isActive = ordenamiento.campo === campo;
    
    return (
      <TableCell 
        sx={{ 
          color: 'white', 
          fontWeight: 'bold',
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#1565c0' }
        }}
        onClick={() => handleOrdenar(campo)}
        {...props}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>{label || children}</span>
          <Box display="flex" flexDirection="column" alignItems="center">
            <FontAwesomeIcon 
              icon={faSortUp} 
              style={{ 
                fontSize: '10px', 
                color: isActive && ordenamiento.direccion === 'asc' ? '#fff' : 'rgba(255,255,255,0.3)',
                marginBottom: '-2px'
              }} 
            />
            <FontAwesomeIcon 
              icon={faSortDown} 
              style={{ 
                fontSize: '10px', 
                color: isActive && ordenamiento.direccion === 'desc' ? '#fff' : 'rgba(255,255,255,0.3)',
                marginTop: '-2px'
              }} 
            />
          </Box>
        </Box>
      </TableCell>
    );
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Card elevation={3} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h4" gutterBottom sx={{ 
            fontWeight: 'bold', 
            color: '#1976d2',
            borderBottom: '2px solid #1976d2',
            pb: 1
          }}>
            <FontAwesomeIcon icon={faFileInvoice} style={{ marginRight: '10px' }} />
            Validar Abonos
          </Typography>
          
          {/* Estadísticas */}
          {estadisticas && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                    <Typography variant="h6" color="primary">
                      {estadisticas.totalAbonos}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Total Abonos
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e5f5' }}>
                    <Typography variant="h6" color="secondary">
                      Q {estadisticas.totalMonto}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Monto Total
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                    <Typography variant="h6" color="warning.main">
                      {estadisticas.pendientes}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Pendientes
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                    <Typography variant="h6" color="success.main">
                      {estadisticas.validados} ({estadisticas.porcentajeValidados}%)
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Validados
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
          
          <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8f9fa', borderRadius: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  id="fechaInicio"
                  label="Fecha de Inicio"
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <FontAwesomeIcon 
                        icon={faCalendarAlt} 
                        style={{ marginRight: '8px', color: '#666' }}
                      />
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  id="fechaFin"
                  label="Fecha de Fin"
                  type="date"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <FontAwesomeIcon 
                        icon={faCalendarAlt} 
                        style={{ marginRight: '8px', color: '#666' }}
                      />
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <FormControl variant="outlined" fullWidth size="small">
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={filtroEstado}
                    onChange={(e) => setFiltroEstado(e.target.value)}
                    label="Estado"
                  >
                    <MenuItem value="Pendiente de Validar">Pendiente de Validar</MenuItem>
                    <MenuItem value="Validado">Validado</MenuItem>
                    <MenuItem value="">Todos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <StyledButton
                  variant="outlined"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  startIcon={<FontAwesomeIcon icon={faFilter} />}
                  endIcon={<FontAwesomeIcon icon={showAdvancedFilters ? faChevronUp : faChevronDown} />}
                  fullWidth
                >
                  {showAdvancedFilters ? 'Ocultar Filtros' : 'Filtros Avanzados'}
                </StyledButton>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <StyledButton
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FontAwesomeIcon icon={faSearch} />}
                  sx={{
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0'
                    }
                  }}
                  fullWidth
                >
                  {loading ? 'Buscando...' : 'Buscar'}
                </StyledButton>
              </Grid>
            </Grid>
          </Paper>

          {/* Filtros avanzados */}
          <AdvancedFilters
            datosGenerados={datosOriginales}
            filtros={filtrosAvanzados}
            onFiltrosChange={setFiltrosAvanzados}
            camposDisponibles={camposDisponibles}
            open={showAdvancedFilters}
            onToggle={setShowAdvancedFilters}
          />
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Cargando abonos...
          </Typography>
        </Box>
      ) : (
        <>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Resultados: {filteredData.length} abonos encontrados
                  {ordenamiento.campo && (
                    <Chip 
                      size="small"
                      label={`Ordenado por: ${ordenamiento.campo} (${ordenamiento.direccion === 'asc' ? 'Asc' : 'Desc'})`}
                      sx={{ ml: 2 }}
                      onDelete={() => {
                        setOrdenamiento({ campo: 'acov_codigo', direccion: 'asc' });
                        const datosOrdenados = ordenarDatos(datosGenerados, 'acov_codigo', 'asc');
                        setDatosGenerados(datosOrdenados);
                      }}
                    />
                  )}
                </Typography>
                {filtrosAvanzados.textoGeneral && (
                  <Chip 
                    label={`Búsqueda: "${filtrosAvanzados.textoGeneral}"`}
                    color="primary"
                    onDelete={() => setFiltrosAvanzados(prev => ({ ...prev, textoGeneral: '' }))}
                  />
                )}
              </Box>
              
              <Box sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#1976d2' }}>
                      <SortableHeader campo="acov_codigo" label="Código Abono" width="120px" />
                      <SortableHeader campo="Banco" label="Banco" width="150px" />
                      <SortableHeader campo="FormaPago" label="Forma de Pago" width="150px" />
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '200px' }}>OV/Factura</TableCell>
                      <SortableHeader campo="NombreCliente" label="Cliente" width="200px" />
                      <SortableHeader campo="NoReferencia" label="Referencia" width="150px" />
                      <SortableHeader campo="Monto" label="Monto" width="120px" />
                      <SortableHeader campo="Fecha" label="Fecha y Hora" width="150px" />
                      <SortableHeader campo="Proceso" label="Estado" width="150px" />
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '150px' }}>Acciones</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '150px' }}>Comprobante</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '100px' }}>Informe</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {datosPaginados.length > 0 ? (
                      datosPaginados.map(item => {
                        const rowColor = getRowColorByExtension(item.B64Extension);
                        const rowBorder = getRowBorderColorByExtension(item.B64Extension);
                        const isPDF = item.B64Extension?.toLowerCase() === 'pdf';
                        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(item.B64Extension?.toLowerCase());
                        const bancoNormalizado = normalizarBancoVisalink(item);
                        
                        return (
                          <TableRow 
                            key={item.acov_codigo}
                            sx={{ 
                              backgroundColor: rowColor,
                              borderLeft: rowBorder,
                              borderRight: rowBorder,
                              '&:hover': { 
                                backgroundColor: rowColor === 'transparent' 
                                  ? '#f5f5f5' 
                                  : `${rowColor.replace('0.05', '0.1')}` 
                              }
                            }}
                          >
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                {item.B64Extension && (
                                  <FontAwesomeIcon 
                                    icon={getExtensionIcon(item.B64Extension)}
                                    style={{ 
                                      color: getExtensionIconColor(item.B64Extension),
                                      fontSize: '14px'
                                    }}
                                  />
                                )}
                                <Typography sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                  #{item.acov_codigo}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2">{bancoNormalizado}</Typography>
                                {item.FormaPago && item.FormaPago.toLowerCase().includes('visalink') && (
                                  <Chip 
                                    label="Visalink"
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    sx={{ height: '20px', fontSize: '0.6rem' }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{item.FormaPago}</Typography>
                            </TableCell>
                            <TableCell>
                              {formatOVFactura(item)}
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {item.CodigoCliente}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {item.NombreCliente}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{item.NoReferencia}</Typography>
                            </TableCell>
                            <TableCell>
                              {formatMonto(item.Monto)}
                            </TableCell>
                            <TableCell>
                              {formatDateTime(item.Fecha)}
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Chip 
                                  label={item.Proceso}
                                  color={
                                    item.Proceso === 'Validado' ? 'success' : 
                                    item.Proceso === 'Pendiente de Validar' ? 'warning' : 'error'
                                  }
                                  size="small"
                                  sx={{ mb: 0.5 }}
                                />
                                {item.UsuarioValidador && (
                                  <Typography variant="caption" color="textSecondary" display="block">
                                    Por: {item.UsuarioValidador}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {item.Proceso !== "Validado" ? (
                                <Box display="flex" gap={1}>
                                  <Tooltip title="Validar abono">
                                    <IconButton
                                      size="small"
                                      onClick={() => handlePasarAbono(item.acov_codigo)}
                                      sx={{
                                        color: 'green',
                                        '&:hover': {
                                          backgroundColor: 'rgba(0, 128, 0, 0.1)'
                                        }
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faCheck} />
                                    </IconButton>
                                  </Tooltip>
                                  
                                  <Tooltip title="Cancelar abono">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCancelarAbono(item.acov_codigo)}
                                      sx={{
                                        color: 'red',
                                        '&:hover': { backgroundColor: 'rgba(255, 0, 0, 0.1)' }
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faTimes} />
                                    </IconButton>
                                  </Tooltip>
                                  
                                  <Tooltip title="Eliminar abono">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleTrashAbono(item.acov_codigo)}
                                      sx={{
                                        color: 'black',
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' }
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="textSecondary">
                                  No hay acciones disponibles
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {item.B64Imagen && item.B64Extension ? (
                                <Box display="flex" gap={1}>
                                  <FilePreview 
                                    base64={item.B64Imagen}
                                    extension={item.B64Extension}
                                    filename={`abono_${item.acov_codigo}`}
                                    fileType={isPDF ? 'pdf' : 'image'}
                                  />
                                  <Tooltip title={`Descargar ${isPDF ? 'PDF' : 'imagen'}`}>
                                    <IconButton 
                                      size="small"
                                      onClick={() => descargarArchivo(
                                        item.B64Imagen, 
                                        item.B64Extension, 
                                        `abono_${item.acov_codigo}`
                                      )}
                                      color={isPDF ? "error" : "primary"}
                                    >
                                      <FontAwesomeIcon icon={faDownload} />
                                    </IconButton>
                                  </Tooltip>
                                  {(isPDF || isImage) && (
                                    <Chip 
                                      icon={<FontAwesomeIcon icon={isPDF ? faFilePdf : faFileImage} />}
                                      label={isPDF ? 'PDF' : item.B64Extension?.toUpperCase()}
                                      size="small"
                                      color={isPDF ? "error" : "primary"}
                                      variant="outlined"
                                      sx={{ height: '24px' }}
                                    />
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="textSecondary">
                                  Sin archivo
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Generar informe (PDF se convierte automáticamente)">
                                <IconButton
                                  size="small"
                                  onClick={() => handlePrintReport(item)}
                                  sx={{
                                    color: '#1976d2',
                                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' }
                                  }}
                                >
                                  <FontAwesomeIcon icon={faPrint} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                          <Typography variant="h6" color="textSecondary">
                            No se encontraron abonos
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {fechaInicio && fechaFin 
                              ? `para el período ${fechaInicio} al ${fechaFin}`
                              : 'Seleccione un rango de fechas y haga clic en Buscar'
                            }
                          </Typography>
                          {Object.values(filtrosAvanzados.valoresFiltro).some(valor => valor) && (
                            <Button 
                              variant="outlined" 
                              size="small" 
                              sx={{ mt: 2 }}
                              onClick={() => setFiltrosAvanzados(prev => ({ ...prev, valoresFiltro: {} }))}
                            >
                              Limpiar filtros específicos
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
              
              {filteredData.length > 0 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" color="textSecondary">
                      Mostrando {indexPrimerElemento + 1} - {Math.min(indexUltimoElemento, filteredData.length)} de {filteredData.length} abonos
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Filas por página</InputLabel>
                      <Select
                        value={elementosPorPagina}
                        onChange={handleElementosPorPaginaChange}
                        label="Filas por página"
                      >
                        <MenuItem value={10}>10 filas</MenuItem>
                        <MenuItem value={25}>25 filas</MenuItem>
                        <MenuItem value={50}>50 filas</MenuItem>
                        <MenuItem value={100}>100 filas</MenuItem>
                        <MenuItem value={250}>250 filas</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Pagination
                    count={Math.ceil(filteredData.length / elementosPorPagina)}
                    page={paginaActual}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    size="medium"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faCheck} style={{ color: 'green', marginRight: '10px' }} />
            Confirmación de Validación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Typography variant="body1">
            ¿Está seguro de que desea validar el abono #{codigoSeleccionado}?
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 2 }}>
            Esta acción marcará el abono como "Validado" y no se podrá revertir.
          </Typography>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAccept}
            sx={{ backgroundColor: 'green' }}
          >
            Confirmar Validación
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faTimes} style={{ color: 'red', marginRight: '10px' }} />
            Cancelar Abono
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Typography variant="body1" gutterBottom>
            ¿Está seguro de que desea cancelar el abono #{codigoSeleccionado}?
          </Typography>
          <TextField
            label="Motivo de cancelación"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={3}
            placeholder="Ingrese el motivo de la cancelación..."
            required
          />
          <Typography variant="caption" color="textSecondary">
            Este comentario será registrado en el sistema.
          </Typography>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outlined" onClick={handleCloseModal}>
            Cerrar
          </Button>
          <Button 
            variant="contained" 
            onClick={handlecancelAbono}
            disabled={!comentario.trim()}
            sx={{ backgroundColor: 'red' }}
          >
            Confirmar Cancelación
          </Button>
        </Modal.Footer>
      </Modal> 

      <Modal show={showModal2} onHide={handleCloseModal2} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faTrash} style={{ color: 'black', marginRight: '10px' }} />
            Eliminar Abono
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Typography variant="body1" gutterBottom>
            ¿Está seguro de que desea eliminar permanentemente el abono #{codigoSeleccionado}?
          </Typography>
          <Typography variant="body2" color="error" gutterBottom>
            ¡Advertencia! Esta acción no se puede deshacer.
          </Typography>
          <TextField
            label="Motivo de eliminación"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={3}
            placeholder="Ingrese el motivo de la eliminación..."
            required
          />
          <Typography variant="caption" color="textSecondary">
            Este comentario será registrado en el sistema.
          </Typography>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outlined" onClick={handleCloseModal2}>
            Cerrar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleTrashedAbono}
            disabled={!comentario.trim()}
            sx={{ backgroundColor: 'black' }}
          >
            Confirmar Eliminación
          </Button>
        </Modal.Footer>
      </Modal>

      {selectedItemForPrint && (
        <PrintReport 
          item={selectedItemForPrint}
          open={printReportOpen}
          onClose={() => setPrintReportOpen(false)}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Abono;